import { put, call, takeLatest, select, delay, fork } from 'redux-saga/effects';
import actions from 'store/notifications/actions';
import notificationsSelectors from 'store/notifications/selectors';
import types from 'store/notifications/types';
import NotificationsApi from 'services/api/common/NotificationsApi';

import loggerConstants from 'store/logger/constant';
import {
  DISPLAY_NOTIFICATION_CRITICAL,
  DISPLAY_NOTIFICATION_INFO,
  DISPLAY_NOTIFICATION_WARNING,
} from 'domain/notifications/consts';
import {
  renderNotificationTooltipTitle,
  getTooltipTypeByDisplayNotificationType,
} from 'domain/notifications/Services';

function* fetchData() {
  try {
    yield put(actions.fetchNotificationsStart());
    const { data } = yield call(NotificationsApi.getNotificationsList);

    if (data.data.length > 0) {
      const notReadList = data.data.filter((item) => !item.read);
      const notReadListShowModal = data.data.filter(
        (item) =>
          !item.read &&
          item.display_notification_type <= 4 &&
          item.display_notification_type >= 1
      );
      yield put(
        actions.fetchNotificationsSuccess(data.data, notReadListShowModal)
      );

      if (notReadList.length > 0) {
        const [notification] = notReadList;
        const notificationType = getTooltipTypeByDisplayNotificationType(
          notification.display_notification_type
        );
        // If after login, show modal or tooltip base on notification label
        if (localStorage.getItem('after_login_state') === 'is_after_login') {
          localStorage.setItem('after_login_state', 'is_not_after_login');
          // Show modal if label is 1-4
          if (
            notificationType === DISPLAY_NOTIFICATION_CRITICAL ||
            notificationType === DISPLAY_NOTIFICATION_WARNING
          ) {
            yield put(actions.addNotificationModal(notification.id));
            yield put(actions.changeNewsModalStatus(true));
          }
          // Show tooltip A if label is 5-8
          if (
            notificationType === DISPLAY_NOTIFICATION_INFO &&
            !!notification.priority
          ) {
            localStorage.removeItem('after_login_state');
            yield put(
              actions.toggleTooltip({
                isShow: true,
                message: renderNotificationTooltipTitle(
                  DISPLAY_NOTIFICATION_CRITICAL,
                  notification.title,
                  notReadList.length
                ),
                type: 'auto',
              })
            );
          }
          // Show tooltip B if label is 5-8 and priority is false
          if (
            notificationType === DISPLAY_NOTIFICATION_INFO &&
            !notification.priority
          ) {
            localStorage.removeItem('after_login_state');
            yield put(
              actions.toggleTooltip({
                isShow: true,
                message: renderNotificationTooltipTitle(
                  DISPLAY_NOTIFICATION_WARNING,
                  notification.title,
                  notReadList.length
                ),
                type: 'auto',
              })
            );
          }
        } else {
          localStorage.removeItem('after_login_state');
          // Not after login, only show tooltip if have priority notification
          const notReadPriority = notReadList.find((item) => item.priority);
          if (notReadPriority) {
            yield put(
              actions.toggleTooltip({
                isShow: true,
                message: renderNotificationTooltipTitle(
                  DISPLAY_NOTIFICATION_CRITICAL,
                  notReadPriority.title,
                  notReadList.length
                ),
                type: 'auto',
              })
            );
          }
        }
      } else {
        localStorage.removeItem('after_login_state');
      }
      // if (!modalNewsData?.read) yield put(actions.toggleNewsModal());
    }
  } catch (error) {
    yield put(
      actions.fetchNotificationsError({
        error: error.response || error,
        scope: loggerConstants.SCOPE_DONT_SHOW,
      })
    );
  }
}

/**
 * Fallback logic to show notification modal after login if needed.
 * Only runs if login state is 'is_not_after_login' and there are unread notifications.
 * Shows modal for CRITICAL or WARNING notification types.
 */
function* handleShowModal(newNotReadList, isLoginState) {
  if (isLoginState !== 'is_not_after_login') return;
  if (newNotReadList.length === 0) {
    localStorage.removeItem('after_login_state');
    return;
  }
  const notification = newNotReadList[0];
  const notificationType = getTooltipTypeByDisplayNotificationType(
    notification.display_notification_type
  );
  if (
    notificationType === DISPLAY_NOTIFICATION_CRITICAL ||
    notificationType === DISPLAY_NOTIFICATION_WARNING
  ) {
    yield put(actions.updateNotificationNotRead(newNotReadList));
    yield put(actions.addNotificationModal(notification.id));
    yield put(actions.changeNewsModalStatus(true));
  }
}

/**
 * Saga to update notification as read.
 * - Races API call against a 2s delay.
 * - If delay wins, runs fallback logic (shows modal if needed).
 * - If API succeeds, updates notification state.
 * - Ensures fallback logic only runs once (not duplicated).
 */
function* updateReadNotification(args) {
  const { id, isUpdateRead } = args.payload;
  const notReadList = yield select(
    notificationsSelectors.getUnreadNotificationsList
  );
  const isLoginState = localStorage.getItem('after_login_state');
  const newNotReadList = notReadList.filter((item) => item.id !== id);

  try {
    // Fork a saga to run show new modal after 500ms
    yield fork(function* () {
      yield delay(500);
      yield* handleShowModal(newNotReadList, isLoginState);
    });
    if (isUpdateRead) {
      yield call(NotificationsApi.putNotificationById, id);
    }
  } catch (error) {
    console.log(error);
  } finally {
    // update modal list and modal unread list anyway
    if (isUpdateRead)
      yield put(actions.updateNotificationReadSuccess(id, newNotReadList));
  }
}

export default function* notificationsTimeSaga() {
  yield takeLatest(types.FETCH_NOTIFICATIONS, fetchData);
  yield takeLatest(types.UPDATE_NOTIFICATION_READ, updateReadNotification);
}
